import { createUniqueId, type JSX, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CardAnswer } from '../../lib/card-answer';
import { ANSWER_MAX } from '../../lib/machine-card';
import { buttonClass } from '../ui/Button';

// The card's answer box (#6726, prototype blkHTML's `.qa`, `:1444-1447`): a visually hidden
// label, a two-row textarea stopping at 600 characters, "Send answer" and "Cancel". Not the
// Blockers page's `BlockerAnswerForm`: this box answers prose only, and shares that page's
// call and its refusal words instead (`lib/card-answer.ts`).
//
// "Send answer" is `aria-disabled`, never `disabled`, below two trimmed characters and while
// sending, so it stays in the tab order and says why; the press itself is dropped by
// `send`. A refusal keeps the box and the text and shows the Blockers page's message.
// Focus lands in the textarea when the box opens; Escape is Cancel.

export function AnswerBox(props: {
  readonly blockerId: string;
  readonly answer: CardAnswer;
  readonly onCancel: () => void;
}): JSX.Element {
  const id = createUniqueId();
  const errorId = `${id}-error`;
  const a = (): CardAnswer => props.answer;
  let field: HTMLTextAreaElement | undefined;
  onMount(() => field?.focus());
  return (
    <div class="mission-answer">
      <label class="sr-only" for={id}>
        {t('fleet.machines.card_answer_label', { id: props.blockerId })}
      </label>
      <textarea
        ref={field}
        id={id}
        class="textarea mission-answer__field"
        rows="2"
        maxlength={ANSWER_MAX}
        placeholder={t('fleet.machines.card_answer_placeholder')}
        value={a().text(props.blockerId)}
        aria-describedby={a().error(props.blockerId) === null ? undefined : errorId}
        onInput={(event) => a().setText(props.blockerId, event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          props.onCancel();
        }}
      />
      <Show when={a().error(props.blockerId)}>
        {(message) => (
          <p id={errorId} class="mission-answer__error" role="alert">
            {message()}
          </p>
        )}
      </Show>
      <div class="mission-answer__actions">
        <button
          type="button"
          class={buttonClass()}
          aria-disabled={a().ready(props.blockerId) ? undefined : 'true'}
          onClick={() => void a().send(props.blockerId)}
        >
          {t(a().sending(props.blockerId) ? 'blockers.answer_sending' : 'blockers.answer_submit')}
        </button>
        <button type="button" class={buttonClass({ variant: 'ghost' })} onClick={props.onCancel}>
          {t('fleet.machines.card_answer_cancel')}
        </button>
      </div>
    </div>
  );
}
